import type { ReactNode } from "react";
import { buttonClass } from "@/components/common/controls";
import { LockIcon, SparkIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import SavedResume from "./SavedResume";

/** Where a Tailor button sits: the panel beside the inputs, or the phone's strip. */
export type TailorPlace = "panel" | "strip";

/**
 * The Tailor button for each place, or null where there is none: a closed
 * job takes no application. A pasted job's opens the form, and a posting's
 * is the posting page's own button.
 */
export type TailorSlot = ((place: TailorPlace) => ReactNode) | null;

/** The pasted job's way on from the tool: it opens the form that asks which job it is. */
export function TailorButton({
    onTailor,
    place,
    busy,
}: {
    onTailor: () => void;
    place: TailorPlace;
    /** A tailor is being written, so this press would send nothing. */
    busy: boolean;
}) {
    return (
        <button
            {...actionProps("tool_tailor_pressed", { source: "ResumeCheck", section: place })}
            type="button"
            disabled={busy}
            onClick={onTailor}
            className={
                place === "panel"
                    ? buttonClass("primary", "tall", "w-full")
                    : buttonClass("primary", "md")
            }
        >
            <SparkIcon className="size-4" />
            Tailor it for me
        </button>
    );
}

/**
 * What AI tailoring is, the allowance for a visitor, and Tailor once there
 * is a result. A member sees the saved resume Tailor reads above the
 * button, and that the box's text is not it.
 */
export default function TailorBlock({
    title,
    line,
    visitor,
    member,
    tailor,
}: {
    title: string;
    line: string;
    /** A member sees no allowance. */
    visitor: boolean;
    member: boolean;
    /** Left out before a result: see the gap first. */
    tailor?: (place: TailorPlace) => ReactNode;
}) {
    return (
        <>
            <div className="flex flex-col gap-0.5">
                <h3 className="m-0 font-display text-subhead font-bold tracking-[-0.01em]">
                    {title}
                </h3>
                <span className="text-ui text-fg-muted [text-wrap:pretty]">{line}</span>
            </div>
            {member && tailor && <SavedResume />}
            {tailor?.("panel")}
            {member && tailor && (
                <span className="text-[12.5px] text-fg-faint">
                    Tailor uses your saved resume, not the text in the box.
                </span>
            )}
            {visitor && (
                <span className="text-[12.5px] text-fg-faint">
                    <span className="font-mono font-semibold text-fg">10</span> a month, free. No
                    card.
                </span>
            )}
        </>
    );
}

/**
 * The promise. A posting's job goes nowhere, so there it is only the first
 * sentence, and a closed job has no Tailor to wait for. A member's box is
 * only for the check: Tailor reads their saved resume, so it says so.
 */
export function PrivacyNote({
    posting,
    closed,
    member,
}: {
    posting: boolean;
    closed: boolean;
    member: boolean;
}) {
    const untilTailor = "Your resume stays in this browser until you press Tailor.";
    const stays = "The text in the box stays in this browser.";
    return (
        <p className="m-0 flex items-start gap-2 text-[13px] leading-[1.45] text-fg-muted [text-wrap:pretty]">
            <LockIcon className="mt-0.5 size-3.5 shrink-0 text-fg-faint" />
            <span>
                {member
                    ? posting
                        ? stays
                        : `${stays} Only the job's text goes to our server.`
                    : closed
                      ? "Your resume stays in this browser."
                      : posting
                        ? untilTailor
                        : `${untilTailor} Only the job's text goes to our server.`}
            </span>
        </p>
    );
}
